import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../../core/common/dataTable/index";
import { getOtpList } from "../../../core/data/redux/otpLogsSlice";
import { all_routes } from "../../router/all_routes";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { formatDateTime } from "../../../utils/dateUtils";

const OtpLogs = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const otpLogsState = useSelector((state: any) => state.otpLogsState);

  const [dataList, setDataList] = useState<any[]>([]);
  const [query, setQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const fetchRecords = (pagination: any, query: string) => {
    dispatch(
      getOtpList({
        page: pagination.current,
        limit: pagination.pageSize,
        query,
      })
    );
  };

  const handleRefresh = () => {
    // Reset any previous state and fetch fresh data
    fetchRecords(pagination, query);
  };

  const handlePagination = (paginationEvent: any) => {
    const newPagination = {
      current: paginationEvent.current,
      pageSize: paginationEvent.pageSize,
      total: pagination.total, // Keep existing total
    };
    setPagination(newPagination);
    fetchRecords(newPagination, query);
  };

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    // Reset to page 1 when searching
    const resetPagination = {
      ...pagination,
      current: 1,
    };
    setPagination(resetPagination);
    fetchRecords(resetPagination, searchQuery);
  };

  useEffect(() => {
    fetchRecords(pagination, query);
  }, []);

  useEffect(() => {
    if (
      otpLogsState.loading === "fulfilled" &&
      otpLogsState.action === "list"
    ) {
      setDataList(otpLogsState.data?.data?.data || []);
      setPagination((prev) => ({
        ...prev,
        total: otpLogsState.data?.data?.total || 0,
        pageSize: otpLogsState.data?.data?.limit || prev.pageSize,
      }));
    } else if (otpLogsState.loading === "rejected") {
      toast.error(otpLogsState?.error || "Failed to fetch OTP logs");
    }
  }, [otpLogsState.loading, otpLogsState.action]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
              pagination.current * pagination.pageSize -
              pagination.pageSize +
              1
              : index + 1}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id.length - b.id.length,
    },
    {
      title: "EmpId",
      dataIndex: "empId",
      render: (empId: string) => <span>{formatEmpIdForDisplay(empId)}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Otp",
      dataIndex: "otp_code",
      render: (text: string) => (
        <span style={{ color: "#c0282c" }}>{text}</span>
      ),
    },
    {
      title: "Created at",
      dataIndex: "created_at",
      render: (text: string) => (
        <span style={{ color: "" }}>{formatDateTime(text)}</span>
      ),
    },
    {
      title: "Otp Expire At",
      dataIndex: "otp_expires_at",
      render: (text: string) =>
        text ? formatDateTime(text) : "N/A",
    },
    {
      title: "Otp For",
      dataIndex: "otp_for",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Device ID",
      dataIndex: "device_id",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Device Type",
      dataIndex: "device_type",
      render: (text: string) => <span>{text || "N/A"}</span>,
    },
  ];

  return (
    <>
      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Otp Logs</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Administration</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Otp History
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handleRefresh}
                  disabled={otpLogsState.loading === "pending"}
                  title="Refresh OTP Logs"
                >
                  <i
                    className={`ti ti-refresh me-2 ${otpLogsState.loading === "pending" ? "spin" : ""
                      }`}
                  />
                  {otpLogsState.loading === "pending"
                    ? "Refreshing..."
                    : "Refresh"}
                </button>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3">Otp History</h4>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={dataList}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={
                  otpLogsState.loading === "pending" &&
                  otpLogsState.action === "list"
                }
                handleSearch={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OtpLogs;
